/**
 * ClosingPoster - Fullscreen "Avslutningsplakat" overlay
 * Shows Sørlandets Travpark logo, Kvernland Bil sponsor logo,
 * "Velkommen til Sørlandets Travpark", first race start time,
 * and a dynamic race day text.
 * Uses the same wave background as FullscreenOdds/FullscreenBong.
 */

const WAVE_BG = 'https://customer-assets.emergentagent.com/job_37d71e0f-ea96-4348-a27f-5a06f0067365/artifacts/ywps6pb9_file_00000000df9c7243b3636a21f3a725db.png';

import { TravparkLogo } from './TravparkLogo';

export function ClosingPoster({ raceDayText, startTime, sommertravetMode }) {
  // Format start time to "kl. HH:MM"
  let formattedTime = '';
  if (startTime) {
    try {
      const d = new Date(startTime);
      if (!isNaN(d.getTime())) {
        formattedTime = `kl. ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
      } else {
        formattedTime = startTime;
      }
    } catch {
      formattedTime = startTime;
    }
  }

  return (
    <div
      data-testid="closing-poster"
      style={{
        backgroundImage: `url(${WAVE_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#050F23',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {/* Gold-bordered card */}
      <div
        data-testid="closing-poster-card"
        style={{
          backgroundColor: '#050F23',
          borderRadius: '20px',
          border: '3px solid var(--theme-panel-border)',
          boxShadow: '0 0 60px var(--theme-header-gradient), 0 25px 50px rgba(0,0,0,0.5)',
          width: '70%',
          maxWidth: '1100px',
          padding: '60px 80px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '36px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Sørlandets Travpark Logo */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <TravparkLogo height={230} sommertravetMode={sommertravetMode} animated style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))' }} />
        </div>

        {/* Divider line */}
        <div style={{
          width: '60%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, var(--theme-gradient-line), transparent)',
        }} />

        {/* Main text: Velkommen til Sørlandets Travpark */}
        <div
          data-testid="closing-poster-main-text"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '3.6rem',
            fontWeight: 700,
            color: '#FFFFFF',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            textAlign: 'center',
            textShadow: '0 2px 8px rgba(0,0,0,0.4)',
            lineHeight: 1.1,
          }}
        >
          Velkommen til Sørlandets Travpark
        </div>

        {/* First race start time */}
        {formattedTime && (
          <div
            data-testid="closing-poster-start-time"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '2.8rem',
              fontWeight: 600,
              color: 'var(--theme-accent)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              textAlign: 'center',
              textShadow: '0 2px 6px rgba(0,0,0,0.3)',
            }}
          >
            Første løp starter {formattedTime}
          </div>
        )}

        {/* Dynamic race day text */}
        {raceDayText && (
          <div
            data-testid="closing-poster-raceday-text"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '2.2rem',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.8)',
              letterSpacing: '0.03em',
              textAlign: 'center',
            }}
          >
            {raceDayText}
          </div>
        )}

        {/* Divider line */}
        <div style={{
          width: '40%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, var(--theme-gradient-line-subtle), transparent)',
        }} />

        {/* Kvernland Bil sponsor logo with white background */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '16px 40px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}
        >
          <img
            src="/assets/kvernland-bil.png"
            alt="Kvernland Bil"
            data-testid="closing-poster-sponsor-logo"
            style={{
              height: '100px',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>
    </div>
  );
}
